import React, { useState, useEffect } from 'react';
import { Box, Checkbox, Chip, IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CheckCircle as CheckCircleIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import {
  type Comparison,
  type ComparisonItem,
  ComparisonItemType,
  ComparisonStatusEnum,
} from '../../../../../types/BaselineMeasurements';
import { ComparisonEventsListRowDetail } from './ComparisonEventsListRowDetail';
import { TranslationKeys } from '../../../../../translations';
import type { Connection } from '../../../../../types';
import { useCreateEventFromDifferenceMutation } from '../../../../../hooks/api/baseline-measurements';
import { useSnakeBar } from '../../../../../hooks';
import { useIntl } from 'react-intl';
import { ComparisonNewEmployeeDialog } from './ComparisonNewEmployeeDialog';
import { ComparisonDuplicatedInsurerDataDialog } from './ComparisonDuplicatedInsurerDataDialog';
import { ActionButton } from '../../../../../components';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

const statusConfig = {
  without: {
    color: '#166534', // text-green-800
    backgroundColor: '#dcfce7', // bg-green-100
    borderColor: '#bbf7d0', // border-green-200
    icon: <CheckCircleIcon sx={{ fontSize: '1rem', color: '#166534' }} />,
    label: 'No differences',
  },
  discrepancy: {
    color: '#991b1b', // text-red-800
    backgroundColor: '#fee2e2', // bg-red-100
    borderColor: '#fca5a5', // border-red-200
    icon: <ReportProblemIcon sx={{ fontSize: '1rem', color: '#991b1b' }} />,
    label: ' difference(s)',
  },
  without_insurer_data: {
    color: '#991b1b', // text-red-800
    backgroundColor: '#fee2e2', // bg-red-100
    borderColor: '#fca5a5', // border-red-200
    icon: <ReportProblemIcon sx={{ fontSize: '1rem', color: '#991b1b' }} />,
    label: 'No insurer data',
  },
};
interface ComparisonStatusChipProps {
  comparison: Comparison | undefined;
  item: ComparisonItem;
}

const StatusChip: React.FC<ComparisonStatusChipProps> = ({ comparison, item }) => {
  const getStatus = (comparison: Comparison | undefined, item: ComparisonItem): keyof typeof statusConfig => {
    if (!comparison || comparison.insurerDataStatus !== ComparisonStatusEnum.Completed) {
      return 'without_insurer_data';
    }
    if (item.discrepancies.length > 0) {
      return 'discrepancy';
    }
    return 'without';
  };
  const getLabel = (
    comparison: Comparison | undefined,
    item: ComparisonItem,
    status: keyof typeof statusConfig,
  ): string => {
    if (!comparison || comparison.insurerDataStatus !== ComparisonStatusEnum.Completed) {
      return statusConfig[status].label;
    }
    if (item.discrepancies.length > 0) {
      return `${item.discrepancies.length} ${statusConfig[status].label}`;
    }
    return statusConfig[status].label;
  };

  const status = getStatus(comparison, item) || {};
  const config = statusConfig[status] || {};
  const label = getLabel(comparison, item, status);
  return (
    <Chip
      label={label}
      icon={config.icon}
      sx={{
        fontSize: '0.700rem',
        color: config.color,
        backgroundColor: 'transparent',
        borderColor: config.borderColor,
        borderWidth: 0,
        borderStyle: 'none',
        '& .MuiChip-icon': {
          color: config.color,
        },
      }}
    />
  );
};

type ComparisonEventsListRowProps = {
  connectionId: Connection['connectionId'] | undefined;
  comparison: Comparison | undefined;
  item: ComparisonItem;
  onSelectItem?: () => void;
  selected?: boolean;
  itemOpen: boolean;
};

export const ComparisonEventsListRow = ({
  connectionId,
  comparison,
  item,
  onSelectItem,
  selected,
  itemOpen,
}: ComparisonEventsListRowProps) => {
  const intl = useIntl();
  const [open, setOpen] = useState(itemOpen);
  const [openConfirmCreateNewEmployeeDialog, setOpenConfirmCreateNewEmployeeDialog] = React.useState(false);
  const [openDuplicatedInsurerDataDialog, setOpenDuplicatedInsurerDataDialog] = React.useState(false);
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  const { mutate: createEventFromDifferences } = useCreateEventFromDifferenceMutation();

  useEffect(() => {
    setOpen(itemOpen);
  }, [itemOpen]);

  const closeConfirmCreateNewEmployeeDialog = React.useCallback(() => {
    setOpenConfirmCreateNewEmployeeDialog(false);
  }, []);

  /*const onClickNewEmployeeAction = React.useCallback(() => {
    setOpenConfirmCreateNewEmployeeDialog(true);
  }, []);*/

  const onClickShowDuplicatedInsurerDataAction = React.useCallback(() => {
    setOpenDuplicatedInsurerDataDialog(true);
  }, []);

  const closeShowDuplicatedInsurerData = React.useCallback(() => {
    setOpenDuplicatedInsurerDataDialog(false);
  }, []);

  const onConfirmCreateNewEmployee = React.useCallback(() => {
    if (connectionId && item.comparisonItemId && item.itemType === ComparisonItemType.Unknown) {
      setOpenConfirmCreateNewEmployeeDialog(false);
      const method = 'createEventFromDifferences';
      createEventFromDifferences(
        {
          comparisonId: item.comparisonId,
          connectionId: item.connectionId,
          comparisonItemId: item.comparisonItemId,
          newEventType: ComparisonItemType.NewEmployee,
        },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method,
              message: 'New employee created successfully.',
            });
          },
          onError: error => {
            showErrorSnakeBar({ method, message: intl.formatMessage({ id: TranslationKeys.global_errorMsg }) });
          },
        },
      );
    }
  }, [
    connectionId,
    createEventFromDifferences,
    intl,
    item.comparisonId,
    item.comparisonItemId,
    item.connectionId,
    item.itemType,
    showErrorSnakeBar,
    showSuccessSnakeBar,
  ]);

  return (
    <>
      <TableRow
        onClick={() => setOpen(!open)}
        hover
        selected={selected}
        sx={{
          '&:hover': { bgcolor: alpha('#2563eb', 0.04), cursor: 'pointer' },
          ...(selected && {
            bgcolor: alpha('#2563eb', 0.08),
          }),
        }}
      >
        <TableCell padding="checkbox" sx={{ py: 0.5 }} onClick={e => e.stopPropagation()}>
          {item.itemType !== ComparisonItemType.Unknown && (
            <Checkbox checked={selected} onChange={onSelectItem} color="primary" />
          )}
        </TableCell>

        <TableCell sx={{ py: 0.5 }}>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        {/* Item Type */}
        <TableCell sx={{ py: 0.5 }}>
          <Chip
            label={intl.formatMessage({
              id: `baseline.comparison.itemType.${item.itemType?.toLowerCase()}`,
            })}
            sx={{
              fontSize: '0.650rem',
              bgcolor: '#dbeafe',
              color: '#1e40af',
              borderRadius: '0.25rem', // rounded
            }}
          />
        </TableCell>

        {/* Employee Data */}
        <TableCell sx={{ py: 0.5 }}>
          <Box>
            <Typography
              sx={{
                fontWeight: 'medium', // font-medium
                color: '#111827', // text-gray-900
              }}
            >
              {item.employeeName}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem', // text-sm
                color: '#6b7280', // text-gray-500
                mb: 1,
              }}
            >
              {item.employeeBsn}
            </Typography>
          </Box>
        </TableCell>

        {/* Differences */}
        <TableCell sx={{ py: 0.5 }}>
          <Stack spacing={1} alignItems="flex-start">
            <StatusChip comparison={comparison} item={item} />
          </Stack>
        </TableCell>

        {/* Actions */}
        <TableCell sx={{ py: 0.5 }} onClick={e => e.stopPropagation()}>
          <IconButton
            data-testid={'comparison-show-row-details-button'}
            color="primary"
            onClick={() => setOpen(!open)}
            size="small"
            sx={{
              '&:hover': {
                bgcolor: alpha('#2563eb', 0.1),
              },
            }}
          >
            <VisibilityIcon />
          </IconButton>
          {item.duplicateInsurerSource.length > 0 && (
            <ActionButton
              messageId={'baseline.comparison.items.actions.duplicatedInsurerData'}
              disabled={false}
              onClick={onClickShowDuplicatedInsurerDataAction}
              size="small"
              icon={faCircleExclamation}
              variant={'outlined'}
              sx={{
                color: '#991b1b',
                backgroundColor: '#fee2e2',
                borderColor: '#991b1b',
              }}
            />
          )}

          {/*
          {item.itemType === ComparisonItemType.Unknown && (
            <ActionButton
              data-testid={'comparison-new-employee-button'}
              messageId={TranslationKeys['baseline_comparison_itemType_new-employee']}
              disabled={false}
              onClick={onClickNewEmployeeAction}
              size="small"
            />
          )}*/}
        </TableCell>
      </TableRow>
      <ComparisonEventsListRowDetail connectionId={connectionId} open={open} item={item} />
      {openConfirmCreateNewEmployeeDialog && (
        <ComparisonNewEmployeeDialog
          comparisonItem={item}
          onCloseDialog={closeConfirmCreateNewEmployeeDialog}
          onConfirmDialog={onConfirmCreateNewEmployee}
          openNewEmployeeDialog={openConfirmCreateNewEmployeeDialog}
        />
      )}
      {openDuplicatedInsurerDataDialog && (
        <ComparisonDuplicatedInsurerDataDialog
          comparisonItem={item}
          onCloseDialog={closeShowDuplicatedInsurerData}
          onConfirmDialog={closeShowDuplicatedInsurerData}
          openDialog={openDuplicatedInsurerDataDialog}
        />
      )}
    </>
  );
};
